import { createLogger } from "../utils/logger.js";
import { createCss3dRendererManager } from "../visualization/css3d-renderer-manager.js";
import { createSceneHierarchy, setArPose, setArWorldGroup, setCamera, setScene } from "./webxr-session.js";
import { createCameraFollower } from "../visualization/camera-follower.js";
import { createAlignmentLerper } from "../visualization/alignment-lerper.js";
import { createGpsCompassCubes } from "../visualization/gps-compass-cubes.js";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
//#region ../src/ar/replay-scene.ts
/**
* Replay Scene Module
*
* Sets up a standard Three.js rendering environment for desktop replay mode
* (no WebXR). Uses createSceneHierarchy() for the scene graph, then
* reparents the camera to the scene root (Issue 5 fix) so orbit and FPS
* controls operate in a stable world-space frame, unaffected by the
* alignment matrix on arWorldGroup or odom pose updates on arpose.
*
* Registers the scene objects with webxr-session.ts module-global getters
* (Risk R1 fix) so existing visualizers work without modification.
*
* Camera modes:
*   - orbit (default): OrbitControls — click-drag orbit + scroll zoom
*   - fps: Drag-based mouse look + WASD — left-click-drag rotates camera,
*     WASD moves, Space/Shift for vertical movement (Issue 6)
*
* @see docs/2026-02-19-replay-mode.md Issue 4, Issue 5, Risk R1, R5
* @see docs/2026-03-12-user-feedback.md Issue 5
*/
const log = createLogger("ReplayScene");
let state = null;
/** WASD key tracking for FPS mode */
const keysPressed = {};
/** Movement speed in world-units per second (≈0.15 per frame × 60 fps). */
const FPS_MOVE_SPEED = 9;
/** Reusable scratch vectors — avoids per-frame allocations in updateFpsMovement. */
const _direction = new THREE.Vector3();
const _right = new THREE.Vector3();
const MOUSE_SENSITIVITY = .002;
const MAX_PITCH = Math.PI / 2 - .01;
let isDragging = false;
let lastPointerX = 0;
let lastPointerY = 0;
let fpsPitch = 0;
let fpsYaw = 0;
const _euler = new THREE.Euler(0, 0, 0, "YXZ");
let savedTabindex = { state: "none" };
function onKeyDown(e) {
	keysPressed[e.code] = true;
}
function onKeyUp(e) {
	keysPressed[e.code] = false;
}
function updateFpsMovement(dt) {
	if (!state || state.cameraMode !== "fps") return;
	const speed = FPS_MOVE_SPEED * dt;
	const camera = state.camera;
	camera.getWorldDirection(_direction);
	_right.crossVectors(_direction, camera.up).normalize();
	if (keysPressed["KeyW"]) camera.position.addScaledVector(_direction, speed);
	if (keysPressed["KeyS"]) camera.position.addScaledVector(_direction, -speed);
	if (keysPressed["KeyA"]) camera.position.addScaledVector(_right, -speed);
	if (keysPressed["KeyD"]) camera.position.addScaledVector(_right, speed);
	if (keysPressed["Space"]) camera.position.y += speed;
	if (keysPressed["ShiftLeft"] || keysPressed["ShiftRight"]) camera.position.y -= speed;
}
/**
* Initialize the replay scene with a standard Three.js renderer.
*
* - Creates the scene hierarchy via createSceneHierarchy()
* - Reparents camera from arWorldGroup to scene root (Risk R5)
* - Creates a WebGLRenderer WITHOUT WebXR
* - Registers scene with module-global getters (Risk R1)
* - Inserts canvas into the provided container
* - Starts a requestAnimationFrame render loop
* - Sets up OrbitControls as default camera mode
*
* @param container - DOM element to insert the canvas into
* @returns Scene state object for downstream use
* @throws If already initialized (call disposeReplayScene first)
*/
function initReplayScene(container) {
	if (state) throw new Error("Replay scene already initialized. Call disposeReplayScene() first.");
	const { scene, arWorldGroup, arpose, camera } = createSceneHierarchy();
	scene.add(camera);
	camera.position.set(0, 5, 10);
	camera.lookAt(0, 0, 0);
	const renderer = new THREE.WebGLRenderer({
		antialias: true,
		alpha: true
	});
	renderer.setPixelRatio(window.devicePixelRatio);
	const width = container.clientWidth || window.innerWidth;
	const height = container.clientHeight || window.innerHeight;
	renderer.setSize(width, height);
	renderer.xr.enabled = false;
	container.appendChild(renderer.domElement);
	const css3dManager = createCss3dRendererManager(container, width, height);
	setScene(scene);
	setArWorldGroup(arWorldGroup);
	setArPose(arpose);
	setCamera(camera);
	const orbitControls = new OrbitControls(camera, renderer.domElement);
	orbitControls.enableDamping = true;
	orbitControls.target.set(0, 0, 0);
	const alignmentLerper = createAlignmentLerper(arWorldGroup);
	const cameraFollower = createCameraFollower(scene);
	createGpsCompassCubes(cameraFollower.object3D);
	state = {
		scene,
		arWorldGroup,
		camera,
		renderer,
		orbitControls,
		cameraMode: "orbit",
		rAFId: startRenderLoop(renderer, scene, camera, orbitControls, css3dManager),
		container,
		cameraFollower,
		alignmentLerper,
		css3dManager
	};
	log.info("Replay scene initialized");
	return {
		scene,
		arWorldGroup,
		camera,
		renderer
	};
}
/**
* Dispose the replay scene, cleaning up all resources.
*
* - Cancels the rAF render loop
* - Disposes renderer (frees GPU resources)
* - Disposes camera controls
* - Removes canvas from DOM
* - Clears module-global scene references
*
* Safe to call multiple times (idempotent).
*/
function disposeReplayScene() {
	if (!state) return;
	cancelAnimationFrame(state.rAFId);
	state.alignmentLerper.dispose();
	state.cameraFollower.dispose();
	state.css3dManager.dispose();
	state.orbitControls.dispose();
	if (state.cameraMode === "fps") removeFpsListeners();
	state.container.removeEventListener("keydown", onKeyDown);
	state.container.removeEventListener("keyup", onKeyUp);
	restoreTabindex();
	if (state.renderer.domElement.parentElement) state.renderer.domElement.parentElement.removeChild(state.renderer.domElement);
	state.renderer.dispose();
	setScene(null);
	setArWorldGroup(null);
	setArPose(null);
	setCamera(null);
	state = null;
	log.info("Replay scene disposed");
}
/**
* Get the current replay scene state, or null if not initialized.
*/
function getReplayState() {
	return state;
}
/** Scratch vector for orbit-target follow delta — avoids per-call allocation. */
const _targetDelta = new THREE.Vector3();
/**
* Update the OrbitControls target position and translate the camera
* by the same delta so the orbit viewing relationship is preserved.
*
* OrbitControls.update() does NOT automatically move the camera when
* the target changes — it recomputes offset = camera.position - target
* each frame, which is a no-op without user input. Without explicit
* camera translation, the camera would sit still while the trajectory
* moves away. Moving the camera by the target delta keeps the viewing
* angle and distance constant as the trajectory progresses.
*
* Call this as new odom poses are replayed so the orbit camera
* auto-follows the recorded trajectory.
*
* No-op if not initialized.
*/
function updateOrbitTarget(position) {
	if (!state) return;
	_targetDelta.copy(position).sub(state.orbitControls.target);
	state.camera.position.add(_targetDelta);
	state.orbitControls.target.copy(position);
}
/**
* Get the current camera control mode.
* Returns 'orbit' if not initialized.
*/
function getCameraMode() {
	return state?.cameraMode ?? "orbit";
}
/**
* Get the camera follower's Object3D, or null if the replay scene
* is not initialized. Use this to attach children (e.g., map mesh)
* that should track the camera's position but stay GPS-aligned.
*/
function getCameraFollower() {
	return state?.cameraFollower.object3D ?? null;
}
/**
* Get the alignment lerper, or null if not initialized.
* Used by replay-mode to route store-subscriber alignment updates
* through the lerper instead of applying them directly.
*/
function getAlignmentLerper() {
	return state?.alignmentLerper ?? null;
}
/**
* Get the CSS3D renderer manager, or null if not initialized.
* Used for external resize handling or to verify the CSS3D layer exists.
*/
function getCss3dManager() {
	return state?.css3dManager ?? null;
}
/**
* Toggle between orbit and FPS camera modes.
*
* - orbit → fps: Disables OrbitControls, creates and enables
*   PointerLockControls + WASD keyboard handler
* - fps → orbit: Disables PointerLockControls, re-enables OrbitControls
*
* No-op if not initialized.
*/
function toggleCameraMode() {
	if (!state) return;
	if (state.cameraMode === "orbit") switchToFps();
	else switchToOrbit();
}
function startRenderLoop(renderer, scene, camera, orbitControls, css3dManager) {
	const timer = new THREE.Timer();
	function animate() {
		if (!state) return;
		state.rAFId = requestAnimationFrame(animate);
		timer.update();
		const dt = timer.getDelta();
		if (state.cameraMode === "orbit") orbitControls.update();
		else updateFpsMovement(dt);
		state.alignmentLerper.update(dt);
		state.cameraFollower.update(state.camera, dt);
		renderer.render(scene, camera);
		css3dManager.render(scene, camera);
	}
	return requestAnimationFrame(animate);
}
function onPointerDown(e) {
	if (e.button !== 0) return;
	isDragging = true;
	lastPointerX = e.clientX;
	lastPointerY = e.clientY;
}
function onPointerMove(e) {
	if (!isDragging || !state) return;
	const dx = e.clientX - lastPointerX;
	const dy = e.clientY - lastPointerY;
	lastPointerX = e.clientX;
	lastPointerY = e.clientY;
	fpsYaw -= dx * MOUSE_SENSITIVITY;
	fpsPitch -= dy * MOUSE_SENSITIVITY;
	fpsPitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, fpsPitch));
	_euler.set(fpsPitch, fpsYaw, 0, "YXZ");
	state.camera.quaternion.setFromEuler(_euler);
}
function onPointerUp() {
	isDragging = false;
}
/** Restore the container's tabindex to its pre-FPS-mode value. */
function restoreTabindex() {
	if (!state || savedTabindex.state === "none") return;
	if (savedTabindex.value === void 0) state.container.removeAttribute("tabindex");
	else if (savedTabindex.value !== state.container.getAttribute("tabindex")) state.container.setAttribute("tabindex", savedTabindex.value);
	savedTabindex = { state: "none" };
}
function addFpsListeners() {
	if (!state) return;
	const canvas = state.renderer.domElement;
	canvas.addEventListener("pointerdown", onPointerDown);
	canvas.addEventListener("pointermove", onPointerMove);
	canvas.addEventListener("pointerup", onPointerUp);
}
function removeFpsListeners() {
	if (!state) return;
	const canvas = state.renderer.domElement;
	canvas.removeEventListener("pointerdown", onPointerDown);
	canvas.removeEventListener("pointermove", onPointerMove);
	canvas.removeEventListener("pointerup", onPointerUp);
	isDragging = false;
}
function switchToFps() {
	if (!state) return;
	state.orbitControls.enabled = false;
	_euler.setFromQuaternion(state.camera.quaternion, "YXZ");
	fpsYaw = _euler.y;
	fpsPitch = _euler.x;
	addFpsListeners();
	const currentTabindex = state.container.hasAttribute("tabindex") ? state.container.getAttribute("tabindex") ?? "" : void 0;
	savedTabindex = {
		state: "saved",
		value: currentTabindex
	};
	if (currentTabindex === void 0) state.container.setAttribute("tabindex", "0");
	state.container.focus();
	state.container.addEventListener("keydown", onKeyDown);
	state.container.addEventListener("keyup", onKeyUp);
	state.cameraMode = "fps";
	log.info("Switched to FPS camera mode");
}
function switchToOrbit() {
	if (!state || state.cameraMode === "orbit") return;
	state.cameraMode = "orbit";
	removeFpsListeners();
	state.container.removeEventListener("keydown", onKeyDown);
	state.container.removeEventListener("keyup", onKeyUp);
	restoreTabindex();
	state.orbitControls.enabled = true;
	for (const k of Object.keys(keysPressed)) keysPressed[k] = false;
	log.info("Switched to Orbit camera mode");
}
//#endregion
export { disposeReplayScene, getAlignmentLerper, getCameraFollower, getCameraMode, getCss3dManager, getReplayState, initReplayScene, toggleCameraMode, updateOrbitTarget };
